/**
 * blockUtils.js
 * ─────────────
 * Shared utility functions used across all CMS block components.
 *
 * All functions are pure and have no side-effects so they can be
 * imported and used freely inside Vue computed properties and methods.
 *
 * DB-COMPATIBILITY NOTE: These helpers operate on *values* only.
 * The prop names that get persisted to the DB are defined in each
 * block component and are intentionally never changed here, ensuring
 * that all existing saved block data continues to load correctly.
 */

// ─────────────────────────────────────────────────────────────────────────────
// String helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Capitalise the first character of a string.
 * @param {string} str
 * @returns {string}
 */
import { variantUrl } from './imageSrc.js';
export function capitalize(str) {
	if (!str) return '';
	return str.charAt(0).toUpperCase() + str.slice(1);
}

// ─────────────────────────────────────────────────────────────────────────────
// Typography
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a CSS style object from a map of typography field values.
 *
 * @param {object} cfg - Flat map of typography values.
 *   Recognised keys (all optional):
 *     fontSize, fontFamily, fontWeight, fontStyle,
 *     lineHeight, letterSpacing, color,
 *     textTransform, textDecoration, textAlign,
 *     marginTop, marginBottom
 * @returns {object} CSS style object safe to bind with :style.
 *
 * Convention:
 *   - Empty-string values are skipped (treated as "not set").
 *   - 'none' for textTransform / textDecoration is the browser default
 *     so it is also skipped to keep the emitted style minimal.
 *   - 'normal' for fontStyle is the browser default and is also skipped.
 *   - marginTop / marginBottom are included even when '0' because '0'
 *     is an explicit override that intentionally resets the UA stylesheet.
 *   - Gradient values (e.g. linear-gradient(...)) are applied via
 *     background-image + background-clip: text for gradient text rendering,
 *     since the CSS `color` property does not accept gradient functions.
 */
export function buildTypographyStyle(cfg) {
	var s = {};
	if (!cfg) return s;

	if (cfg.textAlign)    s.textAlign    = cfg.textAlign;
	if (cfg.fontSize)     s.fontSize     = cfg.fontSize;
	if (cfg.fontFamily)   s.fontFamily   = cfg.fontFamily;
	if (cfg.fontWeight)   s.fontWeight   = cfg.fontWeight;
	if (cfg.lineHeight)   s.lineHeight   = cfg.lineHeight;
	if (cfg.letterSpacing) s.letterSpacing = cfg.letterSpacing;

	if (cfg.color) {
		var isGradient = cfg.color.indexOf('gradient') !== -1;

		if (isGradient) {
			// Gradient text: use background-clip technique since the CSS
			// `color` property does not accept gradient functions.
			s.background = cfg.color;
			s.WebkitBackgroundClip = 'text';
			s.backgroundClip = 'text';
			s.WebkitTextFillColor = 'transparent';
			// Constrain width to the text so the gradient doesn't stretch
			// across the full container width.
			s.width = 'fit-content';
			s.maxWidth = '100%';
		} else {
			// Solid colors and CSS variables (var(--...)) both work with
			// the standard `color` property. Using background-clip for
			// variables caused width:fit-content which broke text-align.
			s.color = cfg.color;
		}
	}

	if (cfg.fontStyle && cfg.fontStyle !== 'normal') {
		s.fontStyle = cfg.fontStyle;
	}
	if (cfg.textTransform && cfg.textTransform !== 'none') {
		s.textTransform = cfg.textTransform;
	}
	if (cfg.textDecoration && cfg.textDecoration !== 'none') {
		s.textDecoration = cfg.textDecoration;
	}

	// Spacing: include even when the value is '0' (explicit override).
	if (cfg.marginTop    !== undefined && cfg.marginTop    !== null && cfg.marginTop    !== '') {
		s.marginTop = cfg.marginTop;
	}
	if (cfg.marginBottom !== undefined && cfg.marginBottom !== null && cfg.marginBottom !== '') {
		s.marginBottom = cfg.marginBottom;
	}

	return s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Alignment
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps 'left' | 'center' | 'right' to the corresponding flex value.
 * Exported so blocks that need the resolved value (e.g. alignItems) can use it
 * without duplicating the lookup table.
 */
export var ALIGNMENT_MAP = { left: 'flex-start', center: 'center', right: 'flex-end' };

/**
 * Build a flex alignment style object.
 *
 * @param {string} alignment - 'left' | 'center' | 'right'
 * @param {object} [extraProps] - Additional CSS properties merged into the result.
 * @returns {object} CSS style object.
 *
 * @example
 * // Button / video embed (row flex, justify-content)
 * buildAlignmentStyle(this.alignment, { padding: '1rem' })
 *
 * // Divider (row flex, custom height)
 * buildAlignmentStyle(this.alignment, { padding: '0 1rem', minHeight: this.height, alignItems: 'center' })
 */
export function buildAlignmentStyle(alignment, extraProps) {
	var style = { display: 'flex', justifyContent: ALIGNMENT_MAP[alignment] || 'center' };
	if (extraProps) {
		var keys = Object.keys(extraProps);
		for (var i = 0; i < keys.length; i++) {
			style[keys[i]] = extraProps[keys[i]];
		}
	}
	return style;
}

// ─────────────────────────────────────────────────────────────────────────────
// HTML sanitisation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Defence-in-depth HTML sanitisation.
 *
 * Removes <script> tags (including content) and inline event-handler
 * attributes (on*="...") from an HTML string before it is rendered
 * via v-html. This is a secondary guard — the primary sanitisation
 * should happen at the data layer (DOMPurify / server-side).
 *
 * @param {string} html - Raw HTML string.
 * @returns {string} Cleaned HTML string.
 */
export function sanitizeHtml(html) {
	if (!html) return '';
	var cleaned = String(html);

	// Remove <script>...</script> blocks (including content).
	// Loop until stable so nested-tag bypasses (<scr<script>ipt>) are caught.
	var tag = 'scr' + 'ipt'; // avoid bundler/linter false-positives
	var blockPat = new RegExp('<' + tag + '[\\s\\S]*?<\\/' + tag + '>', 'gi');
	var openPat = new RegExp('<' + tag + '[^>]*>', 'gi');
	var before;
	do {
		before = cleaned;
		cleaned = cleaned.replace(blockPat, '');
		cleaned = cleaned.replace(openPat, '');
	} while (cleaned !== before);

	// Remove inline event handlers: onXxx="..." / onXxx='...' / onXxx=value.
	// Matches whitespace OR '/' OR quote as the separator (e.g. <img/onerror=...>)
	// and preserves the separator character so surrounding markup stays intact.
	cleaned = cleaned.replace(/([\s/"'])on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '$1');

	// Neutralise dangerous URL schemes in href/src/action attributes
	// (javascript:, vbscript:, and data:text/html). data:image/* stays allowed.
	cleaned = cleaned.replace(/((?:href|src|action|xlink:href)\s*=\s*["']?)\s*(?:javascript|vbscript)\s*:/gi, '$1blocked:');
	cleaned = cleaned.replace(/((?:href|src|action|xlink:href)\s*=\s*["']?)\s*data\s*:\s*text\/html/gi, '$1blocked:text/html');

	return cleaned;
}

/**
 * Returns true when a user-supplied URL uses a safe scheme for links/images.
 * Allows http(s), mailto:, tel:, protocol-relative, relative paths and
 * fragment/query-only URLs. Blocks javascript:, vbscript:, data:, etc.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isSafeUrl(url) {
	if (!url) return false;
	var trimmed = String(url).trim();
	// Relative URLs, fragments and query strings carry no scheme
	if (/^(\/|\.\/|\.\.\/|#|\?)/.test(trimmed)) return true;
	// Protocol-relative
	if (trimmed.indexOf('//') === 0) return true;
	// No scheme at all (e.g. "example.com/page")
	if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return true;
	return /^(https?|mailto|tel):/i.test(trimmed);
}

/**
 * Builds an embed custom-element tag string safe for v-html injection.
 * Attribute KEYS are validated against a strict allowlist pattern —
 * an attacker-controlled key like `x="" onmouseover="..."` would otherwise
 * inject arbitrary attributes/handlers. Attribute values are quote-escaped.
 *
 * Shared by embedBlock.vue and embedBlockAdmin.vue.
 *
 * @param {string} tagName - The custom element tag (from the embed catalog).
 * @param {Object} attrs   - Parsed attribute map.
 * @returns {string}
 */
export function buildEmbedTag(tagName, attrs) {
	if (!tagName || !/^[a-zA-Z][a-zA-Z0-9-]*$/.test(tagName)) return '';

	var pairs = [];
	for (var key in attrs) {
		if (!Object.prototype.hasOwnProperty.call(attrs, key)) continue;
		// Strict key allowlist: letters, digits, hyphens; must not be an event handler
		if (!/^[a-zA-Z][a-zA-Z0-9-]*$/.test(key) || /^on/i.test(key)) continue;
		var val = attrs[key];
		if (val !== '' && val !== null && val !== undefined && val !== false) {
			pairs.push(key + '="' + String(val).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;') + '"');
		}
	}

	if (!pairs.length) {
		return '<' + tagName + '></' + tagName + '>';
	}
	return '<' + tagName + ' ' + pairs.join(' ') + '></' + tagName + '>';
}

/**
 * Validate an HTML string for common structural errors.
 *
 * Uses a stack-based tag matcher to check for:
 * - Unclosed tags (mismatched open/close counts for non-void elements)
 * - Stray closing tags without a matching opener
 * - Mismatched open/close tag pairs
 *
 * @param {string} html - Raw HTML string to validate.
 * @returns {{ valid: boolean, errors: string[] }}
 *   `valid` is true when no structural issues are found.
 *   `errors` is an array of human-readable error messages.
 */
export function validateHtml(html) {
	var errors = [];
	if (!html || !html.trim()) {
		return { valid: true, errors: errors };
	}

	// Void (self-closing) elements that don't need a closing tag
	var VOID_ELEMENTS = [
		'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
		'link', 'meta', 'param', 'source', 'track', 'wbr'
	];

	var tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*\/?>/g;
	var stack = [];
	var match;

	while ((match = tagPattern.exec(html)) !== null) {
		var fullTag = match[0];
		var tagName = match[1].toLowerCase();

		// Skip void elements and self-closing tags like <br/> or <img ... />
		if (VOID_ELEMENTS.indexOf(tagName) !== -1) continue;
		if (fullTag.charAt(fullTag.length - 2) === '/') continue;

		var isClosing = fullTag.charAt(1) === '/';

		if (isClosing) {
			if (stack.length === 0) {
				errors.push('Unexpected closing tag </' + tagName + '> with no matching opening tag.');
			} else if (stack[stack.length - 1] !== tagName) {
				var expected = stack[stack.length - 1];
				errors.push('Mismatched tag: expected </' + expected + '> but found </' + tagName + '>.');
				stack.pop();
			} else {
				stack.pop();
			}
		} else {
			stack.push(tagName);
		}
	}

	// Any tags left on the stack are unclosed
	for (var i = stack.length - 1; i >= 0; i--) {
		errors.push('Unclosed tag <' + stack[i] + '>.');
	}

	return { valid: errors.length === 0, errors: errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Date formatting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a date string for display.
 *
 * @param {string} dateStr - ISO 8601 or any Date-constructor-compatible string.
 * @param {string|string[]} [locales='en-US'] - BCP 47 locale tag(s).
 * @param {object} [options] - Intl.DateTimeFormat options.
 *   Defaults to { month: 'short', day: 'numeric', year: 'numeric' }.
 * @returns {string} Localised date string, or '' on failure.
 */
export function formatPostDate(dateStr, locales, options) {
	if (!dateStr) return '';
	try {
		var d = new Date(dateStr);
		if (isNaN(d.getTime())) return '';
		return d.toLocaleDateString(
			locales || 'en-US',
			options || { month: 'short', day: 'numeric', year: 'numeric' }
		);
	} catch (e) {
		return typeof dateStr === 'string' ? dateStr : '';
	}
}

// ─────────────────────────────────────────────────────────────────────────────
// Background
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a CSS style object from a map of background field values.
 *
 * @param {object} cfg - Flat map of background values.
 *   Recognised keys (all optional):
 *     backgroundColor, backgroundImage, backgroundSize, backgroundPosition,
 *     backgroundRepeat
 * @returns {object} CSS style object safe to bind with :style.
 *
 * Convention:
 *   - Empty-string values are skipped (treated as "not set").
 *   - backgroundImage is wrapped with url() if not already wrapped.
 *   - When backgroundImage is set without backgroundSize, 'cover' is used.
 *   - When backgroundImage is set without backgroundPosition, 'center center' is used.
 *   - When backgroundImage is set without backgroundRepeat, 'no-repeat' is used.
 */
export function buildBackgroundStyle(cfg) {
	var s = {};
	if (!cfg) return s;

	if (cfg.backgroundColor) {
		s.backgroundColor = cfg.backgroundColor;
	}

	if (cfg.backgroundImage) {
		s.backgroundImage = cssBackgroundImage(cfg.backgroundImage);
		s.backgroundSize = cfg.backgroundSize || 'cover';
		s.backgroundPosition = cfg.backgroundPosition || 'center center';
		s.backgroundRepeat = cfg.backgroundRepeat || 'no-repeat';
	}

	return s;
}

/**
 * Convert a stored background image value into a valid CSS background-image
 * value. Plain URLs are wrapped with url(); CSS gradients and values already
 * wrapped in url() are passed through unchanged.
 *
 * @param {string} value
 * @returns {string}
 */
export function cssBackgroundImage(value) {
	if (!value) return '';
	var v = String(value).trim();
	if (v.indexOf('url(') !== -1 || v.indexOf('gradient') !== -1) return v;
	// Proxied media gets a 1920px variant (never upscaled, webp when accepted)
	return 'url(' + variantUrl(v, 1920) + ')';
}

/**
 * Build a CSS style object for a background overlay layer.
 *
 * This is intended for a separate overlay <div> positioned absolutely
 * on top of the background image, providing a colour tint.
 *
 * @param {object} cfg - Background values (same as buildBackgroundStyle).
 *   Recognised keys:
 *     backgroundImage, backgroundOverlay, backgroundOverlayOpacity
 * @returns {object} CSS style object for the overlay div, or { display: 'none' }
 *                   if no overlay is needed.
 */
export function buildBackgroundOverlayStyle(cfg) {
	if (!cfg || !cfg.backgroundImage || !cfg.backgroundOverlay) {
		return { display: 'none' };
	}
	var opacity = parseFloat(cfg.backgroundOverlayOpacity);
	if (isNaN(opacity)) opacity = 0.5;
	return {
		position: 'absolute',
		top: '0',
		left: '0',
		right: '0',
		bottom: '0',
		backgroundColor: cfg.backgroundOverlay,
		opacity: String(opacity),
		pointerEvents: 'none'
	};
}

// ─────────────────────────────────────────────────────────────────────────────
// Border
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalise a single border-side object.
 * @param {object|null} s - { width, style, color }
 * @returns {object}
 */
function normBorderSide(s) {
	if (!s) return null;
	var w = (s.width !== undefined && s.width !== null) ? parseInt(s.width, 10) || 0 : 0;
	if (w <= 0) return null;
	return { width: w, style: s.style || 'solid', color: s.color || '#DDDEE0' };
}

/**
 * Build a CSS style object from a responsive border model.
 *
 * Accepts two shapes:
 *   1. Responsive: { desktop: { top, right, bottom, left }, mobile: { … } }
 *   2. Flat (legacy): { top, right, bottom, left }
 *
 * @param {object|null} border - Border model object.
 * @param {boolean} [isMobile=false] - Whether the current viewport is mobile.
 * @returns {object} CSS style object safe to bind with :style.
 */
export function buildBorderStyle(border, isMobile) {
	if (!border) return {};

	// Determine which sides object to use
	var sides;
	if (border.desktop !== undefined || border.mobile !== undefined) {
		// Responsive shape — pick mobile override, fall back to desktop
		if (isMobile && border.mobile) {
			sides = border.mobile;
		} else {
			sides = border.desktop || {};
		}
	} else {
		// Legacy flat shape
		sides = border;
	}

	if (!sides) return {};

	var s = {};
	var names = ['top', 'right', 'bottom', 'left'];
	var cssNames = ['borderTop', 'borderRight', 'borderBottom', 'borderLeft'];

	for (var i = 0; i < names.length; i++) {
		var side = normBorderSide(sides[names[i]]);
		if (side) {
			s[cssNames[i]] = side.width + 'px ' + side.style + ' ' + side.color;
		}
	}

	return s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Boolean coercion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalise a Vue prop that accepts both a real Boolean and the string
 * representation 'true' / 'false' (a pattern common when blocks are
 * serialised to/from JSON in the database).
 *
 * @param {*} val - The raw prop value.
 * @returns {boolean}
 */
export function coerceBool(val) {
	return val === true || val === 'true';
}

/**
 * Text color that reads on a given background: dark text on a light color,
 * light text on a dark one. Understands #rgb, #rrggbb, rgb()/rgba(); anything
 * else (a token, a gradient, empty) returns '' so the caller keeps its default.
 *
 * @param {string} bg CSS color
 * @param {string} [dark='#1E2031']
 * @param {string} [light='#FFFFFF']
 * @returns {string}
 */
export function readableTextOn(bg, dark, light) {
	var rgb = null;
	var s = String(bg || '').trim();
	var m = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
	if (m) {
		var h = m[1].length === 3 ? m[1].split('').map(function (c) { return c + c; }).join('') : m[1];
		rgb = [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
	} else {
		m = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
		if (m) rgb = [Number(m[1]), Number(m[2]), Number(m[3])];
	}
	if (!rgb) return '';
	var lum = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
	return lum > 0.6 ? (dark || '#1E2031') : (light || '#FFFFFF');
}
